import { ClickAwayListener, FormControl, InputLabel, SxProps, Typography, styled } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import { GvTextField, InputFieldError } from "../../../styles/styledComponents";
import React, { useState } from "react";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";

import { Box } from "@mui/system";
import { InfoIcon } from "../../../components/Icons";
import { handleZipCodeChange } from "../../../utils/zipcodeFormat";
import { paletteColors } from "../../../styles/muiTheme";

interface InputControllerProps {
  id?: string;
  name: string;
  label: string;
  control: Control<any>;
  fullWidth?: boolean;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  InputProps?: any;
  inputProps?: any;
  multiline?: boolean;
  sx?: SxProps;
  onBlur?: () => void;
  tooltip?: string;
  noLabel?: boolean;
  labelSx?: SxProps;
  className?: string;
}

const GvInputController = ({
  id,
  name,
  label,
  control,
  fullWidth = true,
  placeholder,
  type = "text",
  disabled = false,
  InputProps,
  inputProps,
  multiline = false,
  sx,
  onBlur,
  tooltip,
  noLabel = false,
  labelSx,
  className,
}: InputControllerProps) => {
  const fieldId = id || name;
  const [open, setOpen] = useState(false);

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: paletteColors.sand.main,
      color: paletteColors.black.main,
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "16px",
      fontFamily: "geograph-regular",
      padding: ".5rem 1rem",
      border: `1px solid ${paletteColors.taupe.dark}`,
      top: ".5rem",
      left: "-1.5rem",
      position: "absolute",
      width: "265px",
      p: {
        fontSize: "12px",
      },
    },
  });

  return (
    <FormControl fullWidth={fullWidth} sx={sx}>
      {label && !noLabel &&
        <InputLabel htmlFor={fieldId} className={`capitalize ${className}`}
          sx={{ display: "flex", alignItems: "center", ...labelSx }}>
          {label}
          {tooltip && (
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <HtmlTooltip
                title={
                  <Typography color="inherit">{tooltip}</Typography>
                }
                placement="right"
                open={open}
              >
                <Box
                  sx={{ display: "grid", ml: .5 }}
                  onMouseEnter={() => setOpen(true)}
                  onMouseLeave={() => setOpen(false)}
                  onTouchStart={() => setOpen(true)}
                >
                  <InfoIcon width={18} height={18} />
                </Box>
              </HtmlTooltip>
            </ClickAwayListener>
          )}
        </InputLabel>
      }
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) =>
          <GvTextField
            id={fieldId}
            type={type}
            placeholder={placeholder}
            error={!!fieldState.error}
            disabled={disabled}
            InputProps={InputProps}
            inputProps={inputProps}
            multiline={multiline}
            rows={2}
            helperText={fieldState.error?.message && <InputFieldError severity="error">
              {fieldState.error?.message}</InputFieldError>}
            {...field}
            onBlur={onBlur}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (name === "zipCode") {
                handleZipCodeChange(e);
              }
              field.onChange(e); // propagate the change to react-hook-form
            }}
          />
        }
      />
    </FormControl>
  );
};

export default GvInputController;
